<template>
  <div class="performance-dashboard">
    <div class="space-y-6">
      <!-- 页面标题 -->
      <div>
        <h2 class="text-2xl font-semibold text-gray-700">系统性能监控</h2>
        <div class="flex items-center mt-2">
          <a href="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</a>
          <span class="mx-2 text-gray-400">•</span>
          <a href="/admin/system/logs" class="text-gray-600 hover:text-gray-800">系统管理</a>
          <span class="mx-2 text-gray-400">•</span>
          <span class="text-gray-600">性能监控</span>
        </div>
      </div>
      
      <!-- 统计卡片 -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <i class="fas fa-microchip text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">CPU使用率</p>
              <p class="text-2xl font-semibold text-gray-800">{{ performanceData.summary.cpu_usage }}%</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">峰值</p>
              <p class="text-sm text-yellow-600 font-semibold">{{ performanceData.cpu_stats.max || '-' }}%</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600">
              <i class="fas fa-memory text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">内存使用率</p>
              <p class="text-2xl font-semibold text-gray-800">{{ performanceData.summary.memory_usage }}%</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">峰值</p>
              <p class="text-sm text-green-600 font-semibold">{{ performanceData.memory_stats.max || '-' }}%</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600">
              <i class="fas fa-network-wired text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">网络流量</p>
              <p class="text-2xl font-semibold text-gray-800">
                {{ performanceData.summary.network_in ? performanceData.summary.network_in + ' MB' : '-' }}
              </p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">出站流量</p>
              <p class="text-sm text-blue-600 font-semibold">
                {{ performanceData.summary.network_out ? performanceData.summary.network_out + ' MB' : '-' }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-purple-100 text-purple-600">
              <i class="fas fa-hdd text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-sm font-medium text-gray-500">磁盘使用率</p>
              <p class="text-2xl font-semibold text-gray-800">{{ performanceData.summary.disk_usage }}%</p>
            </div>
          </div>
          <div class="mt-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-500">可用空间</p>
              <p class="text-sm text-purple-600 font-semibold">{{ 100 - performanceData.summary.disk_usage }}%</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 时间范围选择器 -->
      <div class="bg-white shadow rounded-lg p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <FormSelect
            id="instance-selector"
            label="服务器实例"
            v-model="selectedInstanceId"
            :options="instanceOptions"
            prefixIcon="fas fa-server"
            selectClass="focus:ring-blue-500 focus:border-blue-500"
          />
          
          <FormSelect
            id="time-range-selector"
            label="时间范围"
            v-model="timeRange"
            :options="timeRangeOptions"
            prefixIcon="fas fa-clock"
            selectClass="focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div v-if="isStatic" class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="fas fa-exclamation-triangle text-yellow-400"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700">
                当前显示的是示例数据。{{ errorMessage || '无法连接到AWS CloudWatch或未找到监控数据。' }}
              </p>
            </div>
          </div>
        </div>
        <div v-else-if="partialStatic" class="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="fas fa-info-circle text-blue-400"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm text-blue-700">
                已成功连接到EC2，但部分监控数据不可用。内存和磁盘使用率需要安装CloudWatch Agent才能监控。
              </p>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end mt-4">
          <button 
            @click="refreshData" 
            :disabled="loading"
            class="ml-3 bg-gradient-to-r from-blue-500 to-blue-600 border border-transparent rounded-lg shadow-sm py-2.5 px-5 text-sm font-medium text-white hover:from-blue-600 hover:to-blue-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out"
            :class="{'opacity-75 cursor-not-allowed': loading}"
          >
            <i class="fas fa-filter mr-1"></i> 
            <span v-if="!loading">应用筛选</span>
            <span v-else>加载中...</span>
            <span v-if="loading" class="spinner-border spinner-border-sm ml-1" role="status" aria-hidden="true"></span>
          </button>
        </div>
      </div>
      
      <!-- 性能图表 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- CPU使用率 -->
        <div class="bg-white shadow rounded-md overflow-hidden">
          <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">CPU使用率</h3>
          </div>
          <div class="p-6">
            <div class="flex justify-between items-center text-sm text-gray-600 mb-4">
              <div>峰值: <span class="font-semibold">{{ performanceData.cpu_stats.max || '-' }}%</span></div>
              <div>平均: <span class="font-semibold">{{ performanceData.cpu_stats.avg || '-' }}%</span></div>
              <div>最低: <span class="font-semibold">{{ performanceData.cpu_stats.min || '-' }}%</span></div>
            </div>
            <div class="h-72">
              <div id="cpu-chart-loading" class="chart-loading-overlay" :style="{display: loading ? 'flex' : 'none'}">
                <div class="loading-spinner"></div>
                <p class="mt-2 text-gray-600">数据加载中...</p>
              </div>
              <canvas ref="cpuChart"></canvas>
            </div>
          </div>
        </div>
        
        <!-- 内存使用率 -->
        <div class="bg-white shadow rounded-md overflow-hidden">
          <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">内存使用率</h3>
          </div>
          <div class="p-6">
            <div class="flex justify-between items-center text-sm text-gray-600 mb-4">
              <div>峰值: <span class="font-semibold">{{ performanceData.memory_stats.max || '-' }}%</span></div>
              <div>平均: <span class="font-semibold">{{ performanceData.memory_stats.avg || '-' }}%</span></div>
              <div>最低: <span class="font-semibold">{{ performanceData.memory_stats.min || '-' }}%</span></div>
            </div>
            <div class="h-72">
              <div id="memory-chart-loading" class="chart-loading-overlay" :style="{display: loading ? 'flex' : 'none'}">
                <div class="loading-spinner"></div>
                <p class="mt-2 text-gray-600">数据加载中...</p>
              </div>
              <canvas ref="memoryChart"></canvas>
            </div>
          </div>
        </div>
        
        <!-- 网络流量 -->
        <div class="bg-white shadow rounded-md overflow-hidden">
          <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">网络流量</h3>
          </div>
          <div class="p-6">
            <div class="flex justify-between items-center text-sm text-gray-600 mb-4">
              <div>入站峰值: <span class="font-semibold">{{ performanceData.network_stats?.in_max || '-' }} MB</span></div>
              <div>入站平均: <span class="font-semibold">{{ performanceData.network_stats?.in_avg || '-' }} MB</span></div>
              <div>出站平均: <span class="font-semibold">{{ performanceData.network_stats?.out_avg || '-' }} MB</span></div>
            </div>
            <div class="h-72">
              <div id="network-chart-loading" class="chart-loading-overlay" :style="{display: loading ? 'flex' : 'none'}">
                <div class="loading-spinner"></div>
                <p class="mt-2 text-gray-600">数据加载中...</p>
              </div>
              <canvas ref="networkChart"></canvas>
            </div>
          </div>
        </div>
        
        <!-- 磁盘使用率 -->
        <div class="bg-white shadow rounded-md overflow-hidden">
          <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">磁盘使用率</h3>
          </div>
          <div class="p-6">
            <div class="flex justify-center items-center h-72">
              <div class="chart-container w-full h-full flex justify-center items-center">
                <div id="disk-chart-loading" class="chart-loading-overlay" :style="{display: loading ? 'flex' : 'none'}">
                  <div class="loading-spinner"></div>
                  <p class="mt-2 text-gray-600">数据加载中...</p>
                </div>
                <canvas ref="diskChart" class="max-w-xs"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 实例列表 -->
      <div class="bg-white shadow rounded-md overflow-hidden">
        <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
          <h3 class="text-lg leading-6 font-medium text-gray-900">服务器实例</h3>
          <span v-if="!isStatic" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <i class="fas fa-sync-alt mr-1"></i> 实时数据
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">实例ID</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名称</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP地址</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">启动时间</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="instances.length === 0">
                <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
                  无可用实例数据
                </td>
              </tr>
              <tr v-for="instance in instances" :key="instance.InstanceId" :class="selectedInstanceId === instance.InstanceId ? 'bg-blue-50' : ''">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ instance.InstanceId }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ getTagValue(instance, 'Name', '-') }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ instance.InstanceType }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                        :class="instance.State === 'running' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'">
                    {{ instance.State }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ instance.PublicIpAddress || instance.PrivateIpAddress || '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatLaunchTime(instance.LaunchTime) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- 调试信息区域 -->
      <div v-if="debugMode && ec2Data && ec2Data.length > 0" class="mt-8 bg-gray-100 p-4 rounded-md">
        <h3 class="text-lg font-medium text-gray-900 mb-2">调试信息 (仅开发环境显示)</h3>
        <div class="mb-4">
          <button id="toggleMonitorData" class="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600">
            显示/隐藏监控数据
          </button>
          <button id="toggleEc2Data" class="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 ml-2">
            显示/隐藏EC2数据
          </button>
        </div>
        
        <div id="monitorDataContainer" class="bg-white p-4 rounded-md shadow overflow-auto max-h-96 mb-4" style="display: none;">
          <h4 class="text-md font-medium text-gray-800 mb-2">CloudWatch监控数据</h4>
          <pre class="text-xs text-gray-800">{{ monitorData ? JSON.stringify(monitorData, null, 2) : '无监控数据' }}</pre>
        </div>
        
        <div id="ec2DataContainer" class="bg-white p-4 rounded-md shadow overflow-auto max-h-96" style="display: none;">
          <h4 class="text-md font-medium text-gray-800 mb-2">EC2实例数据</h4>
          <pre class="text-xs text-gray-800">{{ JSON.stringify(ec2Data, null, 2) }}</pre>
        </div>
        
        <p class="mt-2 text-xs text-gray-600">提示: 要获取内存和磁盘使用率数据，需要在EC2实例上安装和配置CloudWatch Agent，指标数据已通过CloudWatch Agent收集。</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, computed, watch } from 'vue';
import Chart from 'chart.js/auto';
import { format, parseISO } from 'date-fns';
import FormSelect from '@/components/common/FormSelect.vue';
import AdminApiClient from '@/utils/admin-api-client';

// 初始化API客户端
const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(true);
const instances = ref([]);
const selectedInstanceId = ref('');
const timeRange = ref('24h');
const isStatic = ref(false);
const partialStatic = ref(false);
const errorMessage = ref('');
const debugMode = ref(false);
const ec2Data = ref(null);
const monitorData = ref(null);

// 实例选项
const instanceOptions = computed(() => {
  return instances.value.map(instance => ({
    value: instance.InstanceId,
    label: `${getTagValue(instance, 'Name', instance.InstanceId)} (${instance.InstanceType})`
  }));
});

// 时间范围选项
const timeRangeOptions = [
  { value: '1h', label: '最近1小时' },
  { value: '6h', label: '最近6小时' },
  { value: '24h', label: '最近24小时' },
  { value: '7d', label: '最近7天' },
  { value: '30d', label: '最近30天' }
];

// 性能数据对象
const performanceData = reactive({
  summary: {
    cpu_usage: 0,
    memory_usage: 0,
    disk_usage: 0,
    network_in: 0,
    network_out: 0
  },
  cpu_stats: { max: 0, avg: 0, min: 0 },
  memory_stats: { max: 0, avg: 0, min: 0 },
  network_stats: { in_max: 0, in_avg: 0, out_avg: 0 },
  cpu_history: [],
  memory_history: [],
  network_in_history: [],
  network_out_history: []
});

// 图表引用
const cpuChart = ref(null);
const memoryChart = ref(null);
const networkChart = ref(null);
const diskChart = ref(null);

// Chart 实例
let cpuChartInstance = null;
let memoryChartInstance = null;
let networkChartInstance = null;
let diskChartInstance = null;

// 安全地获取实例标签值的辅助函数
const getTagValue = (instance, key, defaultValue = '-') => {
  if (!instance.Tags) {
    return defaultValue;
  }
  
  // 如果Tags是关联数组
  if (instance.Tags[key]) {
    return instance.Tags[key];
  }
  
  // 如果Tags是数组的数组
  if (Array.isArray(instance.Tags)) {
    for (const tag of instance.Tags) {
      if (tag.Key === key && tag.Value) {
        return tag.Value;
      }
    }
  }
  
  return defaultValue;
};

// 格式化启动时间
const formatLaunchTime = (launchTime) => {
  if (!launchTime) return '-';
  
  try {
    if (typeof launchTime === 'number') {
      // 处理Unix时间戳
      return format(new Date(launchTime * 1000), 'yyyy-MM-dd HH:mm');
    } else if (typeof launchTime === 'string') {
      return format(parseISO(launchTime), 'yyyy-MM-dd HH:mm');
    } else {
      return String(launchTime);
    }
  } catch (e) {
    return '格式错误: ' + String(launchTime).substring(0, 20);
  }
};

// 加载所有数据
const loadAllData = async () => {
  loading.value = true;
  try {
    await loadInstancesData();
    
    if (instances.value.length > 0 && !selectedInstanceId.value) {
      // 默认选择第一个运行中的实例
      const runningInstance = instances.value.find(i => i.State === 'running');
      selectedInstanceId.value = runningInstance ? runningInstance.InstanceId : instances.value[0].InstanceId;
    }
    
    if (selectedInstanceId.value) {
      await loadPerformanceData();
    }
  } catch (error) {
    console.error('加载数据失败:', error);
    errorMessage.value = error.message || '加载数据失败';
    isStatic.value = true;
  } finally {
    loading.value = false;
  }
};

// 加载 EC2 实例数据
const loadInstancesData = async () => {
  try {
    const response = await apiClient.getSystemPerformance();
    
    if (response && response.success && response.data) {
      instances.value = response.data.instances || [];
      ec2Data.value = response.data.ec2_data || null;
      isStatic.value = response.data.is_static || false;
      partialStatic.value = response.data.partial_static || false;
      errorMessage.value = response.data.error_message || '';
    } else {
      // 如果API响应没有预期数据，设置为静态模式
      isStatic.value = true;
      errorMessage.value = response.message || '获取数据失败';
    }
  } catch (error) {
    console.error('加载实例数据失败:', error);
    errorMessage.value = error.message || '加载实例数据失败';
    isStatic.value = true;
  }
};

// 加载性能数据
const loadPerformanceData = async () => {
  try {
    const response = await apiClient.getSystemPerformance({
      instance_id: selectedInstanceId.value,
      time_range: timeRange.value
    });
    
    if (response && response.success && response.data) {
      const data = response.data.data;
      
      // 更新性能数据
      if (data.summary) {
        performanceData.summary = data.summary;
      }
      
      if (data.cpu_stats) {
        performanceData.cpu_stats = data.cpu_stats;
      }
      
      if (data.memory_stats) {
        performanceData.memory_stats = data.memory_stats;
      }
      
      if (data.network_stats) {
        performanceData.network_stats = data.network_stats;
      }
      
      // 历史数据
      performanceData.cpu_history = data.cpu_history || [];
      performanceData.memory_history = data.memory_history || [];
      performanceData.network_in_history = data.network_in_history || [];
      performanceData.network_out_history = data.network_out_history || [];
      
      // 监控数据
      monitorData.value = data.monitor_data || null;
      
      // 更新图表
      nextTick(() => {
        renderCharts();
      });
    } else {
      // 如果没有获取到数据，显示错误信息
      errorMessage.value = response.message || '获取性能数据失败';
      isStatic.value = true;
    }
  } catch (error) {
    console.error('加载性能数据失败:', error);
    errorMessage.value = error.message || '加载性能数据失败';
    isStatic.value = true;
  }
};

// 刷新数据
const refreshData = async () => {
  loading.value = true;
  try {
    await loadPerformanceData();
  } catch (error) {
    console.error('刷新数据失败:', error);
    errorMessage.value = error.message || '刷新数据失败';
  } finally {
    loading.value = false;
  }
};

// 渲染所有图表
const renderCharts = () => {
  destroyCharts();
  
  // 准备时间标签和数据
  const cpuLabels = performanceData.cpu_history.map(item => item.time);
  const cpuData = performanceData.cpu_history.map(item => item.value);
  
  const memLabels = performanceData.memory_history.map(item => item.time);
  const memData = performanceData.memory_history.map(item => item.value);
  
  const netLabels = performanceData.network_in_history.map(item => item.time);
  const netInData = performanceData.network_in_history.map(item => item.value);
  const netOutData = performanceData.network_out_history.map(item => item.value);
  
  // 确定标签跳过因子
  const cpuSkipFactor = Math.max(1, Math.ceil(cpuLabels.length / 12));
  const memSkipFactor = Math.max(1, Math.ceil(memLabels.length / 12));
  const netSkipFactor = Math.max(1, Math.ceil(netLabels.length / 12));
  
  // 渲染 CPU 图表
  if (cpuChart.value) {
    cpuChartInstance = new Chart(cpuChart.value, {
      type: 'line',
      data: {
        labels: cpuLabels,
        datasets: [{
          label: 'CPU使用率 (%)',
          data: cpuData,
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 2,
          tension: 0.3,
          pointRadius: cpuLabels.length > 30 ? 0 : 3,
          pointHoverRadius: 5,
          spanGaps: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: '使用率 (%)'
            }
          },
          x: {
            title: {
              display: true,
              text: '时间'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 12,
              callback: function(val, index) {
                return index % cpuSkipFactor === 0 ? this.getLabelForValue(val) : '';
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: function(tooltipItems) {
                return '时间: ' + tooltipItems[0].label;
              }
            }
          },
          legend: {
            position: 'top',
          }
        },
        animation: {
          onComplete: function() {
            hideChartLoading('cpu-chart');
          }
        }
      }
    });
  }
  
  // 渲染内存图表
  if (memoryChart.value) {
    memoryChartInstance = new Chart(memoryChart.value, {
      type: 'line',
      data: {
        labels: memLabels,
        datasets: [{
          label: '内存使用率 (%)',
          data: memData,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 2,
          tension: 0.3,
          pointRadius: memLabels.length > 30 ? 0 : 3,
          pointHoverRadius: 5,
          spanGaps: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            title: {
              display: true,
              text: '使用率 (%)'
            }
          },
          x: {
            title: {
              display: true,
              text: '时间'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 12,
              callback: function(val, index) {
                return index % memSkipFactor === 0 ? this.getLabelForValue(val) : '';
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: function(tooltipItems) {
                return '时间: ' + tooltipItems[0].label;
              }
            }
          },
          legend: {
            position: 'top',
          }
        },
        animation: {
          onComplete: function() {
            hideChartLoading('memory-chart');
          }
        }
      }
    });
  }
  
  // 渲染网络图表
  if (networkChart.value) {
    networkChartInstance = new Chart(networkChart.value, {
      type: 'line',
      data: {
        labels: netLabels,
        datasets: [
          {
            label: '入站流量 (MB)',
            data: netInData,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            tension: 0.3,
            pointRadius: netLabels.length > 30 ? 0 : 3,
            pointHoverRadius: 5,
            spanGaps: true
          },
          {
            label: '出站流量 (MB)',
            data: netOutData,
            backgroundColor: 'rgba(153, 102, 255, 0.2)',
            borderColor: 'rgba(153, 102, 255, 1)',
            borderWidth: 2,
            tension: 0.3,
            pointRadius: netLabels.length > 30 ? 0 : 3,
            pointHoverRadius: 5,
            spanGaps: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: '流量 (MB)'
            }
          },
          x: {
            title: {
              display: true,
              text: '时间'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 12,
              callback: function(val, index) {
                return index % netSkipFactor === 0 ? this.getLabelForValue(val) : '';
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: function(tooltipItems) {
                return '时间: ' + tooltipItems[0].label;
              }
            }
          },
          legend: {
            position: 'top',
          }
        },
        animation: {
          onComplete: function() {
            hideChartLoading('network-chart');
          }
        }
      }
    });
  }
  
  // 渲染磁盘图表
  if (diskChart.value) {
    diskChartInstance = new Chart(diskChart.value, {
      type: 'doughnut',
      data: {
        labels: ['已使用', '可用'],
        datasets: [{
          label: '磁盘使用率',
          data: [performanceData.summary.disk_usage, 100 - performanceData.summary.disk_usage],
          backgroundColor: [
            'rgba(153, 102, 255, 0.6)',
            'rgba(201, 203, 207, 0.2)'
          ],
          borderColor: [
            'rgba(153, 102, 255, 1)',
            'rgba(201, 203, 207, 1)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            position: 'bottom',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.label + ': ' + context.raw + '%';
              }
            }
          }
        },
        animation: {
          onComplete: function() {
            hideChartLoading('disk-chart');
          }
        }
      }
    });
  }
};

// 隐藏图表加载动画
const hideChartLoading = (chartId) => {
  const loadingElement = document.getElementById(chartId + '-loading');
  if (loadingElement) {
    loadingElement.style.display = 'none';
  }
};

// 销毁图表
const destroyCharts = () => {
  if (cpuChartInstance) cpuChartInstance.destroy();
  if (memoryChartInstance) memoryChartInstance.destroy();
  if (networkChartInstance) networkChartInstance.destroy();
  if (diskChartInstance) diskChartInstance.destroy();
};

// 设置调试模式切换
const setupDebugButtons = () => {
  const toggleMonitorData = document.getElementById('toggleMonitorData');
  const monitorDataContainer = document.getElementById('monitorDataContainer');
  if (toggleMonitorData && monitorDataContainer) {
    toggleMonitorData.addEventListener('click', function() {
      if (monitorDataContainer.style.display === 'none' || monitorDataContainer.style.display === '') {
        monitorDataContainer.style.display = 'block';
      } else {
        monitorDataContainer.style.display = 'none';
      }
    });
  }
  
  const toggleEc2Data = document.getElementById('toggleEc2Data');
  const ec2DataContainer = document.getElementById('ec2DataContainer');
  if (toggleEc2Data && ec2DataContainer) {
    toggleEc2Data.addEventListener('click', function() {
      if (ec2DataContainer.style.display === 'none' || ec2DataContainer.style.display === '') {
        ec2DataContainer.style.display = 'block';
      } else {
        ec2DataContainer.style.display = 'none';
      }
    });
  }
};

// 设置观察者
const setupWatchers = () => {
  // 观察实例ID变化
  watch(selectedInstanceId, async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await refreshData();
    }
  });
  
  // 观察时间范围变化
  watch(timeRange, async (newRange, oldRange) => {
    if (newRange && newRange !== oldRange) {
      await refreshData();
    }
  });
};

// 组件挂载时加载数据
onMounted(async () => {
  // 判断是否为开发环境并设置调试模式
  debugMode.value = process.env.NODE_ENV === 'development';
  
  // 加载所有数据
  await loadAllData();
  
  // 设置观察者
  setupWatchers();
  
  // 等待DOM更新后设置调试按钮
  nextTick(() => {
    setupDebugButtons();
  });
});
</script>

<style scoped>
table {
  min-width: 100%;
  table-layout: auto;
}

th {
  min-width: 120px;
  white-space: nowrap;
}

.chart-container {
  min-width: 200px;
  min-height: 200px;
  height: 200px;
  position: relative;
}

/* 自定义加载动画 */
.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  vertical-align: text-bottom;
  border: 0.2em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border .75s linear infinite;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* 图表加载动画 */
.chart-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 10;
}

.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 确保图表容器是相对定位 */
.h-72 {
  position: relative;
  height: 18rem;
}

.performance-dashboard {
  padding: 1.5rem;
}

@media (max-width: 768px) {
  th, td {
    min-width: 100px;
  }
  
  .performance-dashboard {
    padding: 1rem;
  }
}
</style>
